import { useState } from "react";
import type { Stripe } from "@stripe/stripe-js";

export interface UsePaymentMethodsOptions {
  stripe: Stripe | null;
}

export interface UsePaymentMethodsReturn {
  createPaymentMethod: (cardElement: any, customerData: any) => Promise<any>;
  loading: boolean;
  error: string | null;
}

/**
 * Simplified hook for creating payment methods with Stripe
 * Only creates PaymentMethod, doesn't save to backend
 */
export function usePaymentMethods(
  options: UsePaymentMethodsOptions,
): UsePaymentMethodsReturn {
  const { stripe } = options;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPaymentMethod = async (cardElement: any, customerData: any) => {
    if (!stripe) {
      throw new Error("Stripe has not loaded yet");
    }

    setLoading(true);
    setError(null);

    try {
      const { paymentMethod, error: stripeError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: customerData.name,
            email: customerData.email,
            phone: customerData.phone,
          },
        });

      if (stripeError) {
        setError(stripeError.message || "Failed to create payment method");
        throw new Error(
          stripeError.message || "Failed to create payment method",
        );
      }

      if (!paymentMethod) {
        throw new Error("Payment method was not created");
      }

      return paymentMethod;
    } catch (err: any) {
      const errorMessage = err.message || "Failed to create payment method";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    createPaymentMethod,
    loading,
    error,
  };
}
